import * as ko from 'knockout';
import { Observable, PureComputed } from 'knockout';

import UploadStatus from '@/App/Types/enums/UploadStatus';

class UploadModel {
  /** An element Id prefix to be used as the basis of a unique DOM element ID */
  elementIdPrefix: string;
  fileName: string;
  fileSize: number;

  uploadId: string | null = null;
  requestId: string | null = null;
  uploadToken: string | null = null;
  publicReference: string | null = null;
  originalComments: string = '';

  nativeBrowserFile: File | null = null;
  ajaxRequest: any = null;

  // Observables
  comments: Observable<string>;
  statusName: Observable<UploadStatus>;
  progress: Observable<number>;
  bytesUploaded: Observable<number>;
  userIsCurrentlyUploading: Observable<boolean>;
  /** Flags if an API request is being made to remove the upload. This API request could fail. */
  isBeingRemoved: Observable<boolean>;

  // Computed observables
  commentHasChanged: PureComputed<boolean> = ko.pureComputed(() => this.comments() !== this.originalComments);
  isAuthorising: PureComputed<boolean> = ko.pureComputed(() => this.statusName() === UploadStatus.authorising);
  addedTimestamp: Date = new Date(1900, 1, 1);

  constructor(serverUpload: any, nativeBrowserFile: File | null) {
    if (serverUpload) {
      this.elementIdPrefix = this.generateElementIdPrefix(serverUpload.fileSize);
      this.fileName = serverUpload.fileName;
      this.fileSize = serverUpload.fileSize;

      this.uploadId = serverUpload.uploadId;
      this.requestId = serverUpload.requestId;
      this.uploadToken = serverUpload.uploadToken;
      this.publicReference = serverUpload.publicReference;
      this.originalComments = serverUpload.comments;
      this.addedTimestamp = new Date(serverUpload.addedTimestamp);
    } else {
      if (nativeBrowserFile === null) {
        throw new Error(`Neither constructor parameters have been provided.`);
      }

      this.elementIdPrefix = this.generateElementIdPrefix(nativeBrowserFile.size);
      this.fileName = nativeBrowserFile.name;
      this.fileSize = nativeBrowserFile.size;

      this.nativeBrowserFile = nativeBrowserFile;
    }

    // Set observables
    this.comments = ko.observable(this.originalComments);
    this.statusName = ko.observable(serverUpload ? serverUpload.uploadStatusName : UploadStatus.authorising );
    this.progress = ko.observable(this.statusName() === UploadStatus.uploaded ? 100 : 0);
    this.bytesUploaded = ko.observable(0);
    this.userIsCurrentlyUploading = ko.observable(false);
    this.isBeingRemoved = ko.observable(false);
  }

  /**
   * Populate upload Id, upload token and others once new server upload created, saved and returned
   * @param {*} serverModel
   */
  updateAfterServerModelCreated(serverModel: any): void {
    this.uploadId = serverModel.uploadId;
    this.uploadToken = serverModel.uploadToken;
    this.publicReference = serverModel.publicReference;
    this.statusName(serverModel.uploadStatusName);
  }

  /**
   * Once the ajax request for a file has been created after the server model is created (in order for
   * the upload token to have been generated) then set the ajax request which will be needed if a user
   * cancels a file upload.
   * @param {*} ajaxRequest Ajax request which can be used to cancel an upload
   */
  setAjaxRequest(ajaxRequest: any): void {
    this.ajaxRequest = ajaxRequest;
  }

  setUploadStarted(): void {
    this.statusName(UploadStatus.uploading);
    this.progress(0);
    this.userIsCurrentlyUploading(true);
  }

  updateUploadProgress(progress: number, bytesUploaded: number): void {
    this.progress(progress);
    this.bytesUploaded(bytesUploaded);
  }

  setUploadComplete(): void {
    this.statusName(UploadStatus.uploaded);
    this.progress(100);
    this.bytesUploaded(this.fileSize);
    this.userIsCurrentlyUploading(false);
  }

  setUploadFailed(): void {
    this.statusName(UploadStatus.failed);
    this.userIsCurrentlyUploading(false);
  }

  setUploadFailedAuthorisation(): void {
    this.statusName(UploadStatus.failedAuthorisation);
  }

  /**
   * Marks an upload as being removed. An API call is being made to remove the upload but this could fail.
   * @param {boolean} isBeingRemoved
   */
  setIsBeingRemoved(isBeingRemoved: boolean): void {
    this.isBeingRemoved(isBeingRemoved);
  }

  cancelUpload(): void {
    this.statusName(UploadStatus.cancelled);
    this.userIsCurrentlyUploading(false);

    if (this.ajaxRequest) {
      this.ajaxRequest.abort();
    }
  }

  /**
   * Generates an element Id prefix string.
   * The string is sufficiently unique enough to be used as the basis of a DOM element Id.
   * @param {number} theFileSize
   * @return {string}
   */
  private generateElementIdPrefix(theFileSize: number): string {
    return 'id-' + Math.random().toString(36).substr(2, 16) + '-' + theFileSize;
  }
}

export default UploadModel;
